import React, { useState } from "react";
import { X } from "lucide-react";

const CaseStudyCards: React.FC = () => {
  const [selectedCase, setSelectedCase] = useState<number | null>(null);

  const caseStudies = [
    {
      name: "Magic Hour (YC)",
      industry: "Media Tech",
      description:
        "Y Combinator-backed media technology company revolutionizing video content creation.",
      gradient: "from-emerald-500 via-teal-300 to-amber-200",
      fullDescription:
        "Magic Hour is a Y Combinator-backed company building the future of video content creation. We helped them automate their content pipeline, reducing production time by 60% and enabling them to scale from 100 to 1,000+ videos per month. Our AI-powered workflow automation handled video processing, metadata generation, and distribution across multiple platforms.",
      impact: [
        "60% reduction in content production time",
        "10x increase in video output capacity",
        "Automated metadata generation for 1,000+ videos/month",
        "Seamless integration with existing creative tools",
      ],
    },
    {
      name: "Snowflake",
      industry: "Data & Analytics",
      description:
        "Enterprise data cloud platform serving thousands of global organizations.",
      gradient: "from-sky-200 via-cyan-300 to-blue-300",
      fullDescription:
        "We partnered with Snowflake to optimize their customer onboarding workflows and internal data processing pipelines. By implementing intelligent automation, we reduced manual data entry by 75% and accelerated customer time-to-value from weeks to days. Our solution integrated seamlessly with their existing infrastructure.",
      impact: [
        "75% reduction in manual data entry",
        "Customer onboarding time cut from weeks to days",
        "Automated processing of 100k+ data records daily",
        "Improved data accuracy and consistency",
      ],
    },
    {
      name: "Hamilton",
      industry: "Ecommerce",
      description:
        "Fast-growing direct-to-consumer brand in home goods and lifestyle products.",
      gradient: "from-lime-300 via-amber-200 to-emerald-400",
      fullDescription:
        "Hamilton needed to scale their e-commerce operations without proportionally scaling their team. We built custom automation for inventory management, order processing, and customer service. The result was a 45% reduction in operational overhead while handling 3x more orders.",
      impact: [
        "45% reduction in operational overhead",
        "3x increase in order processing capacity",
        "Automated inventory sync across 5 sales channels",
        "24/7 AI-powered customer service responses",
      ],
    },
    {
      name: "Terra Foods",
      industry: "Food & Beverage",
      description:
        "Sustainable meal kit company delivering organic products to health-conscious customers.",
      gradient: "from-slate-900 via-teal-900 to-amber-800",
      fullDescription:
        "Terra Foods struggled with coordinating their complex supply chain and meal planning logistics. We developed an AI system that optimized ingredient sourcing, predicted demand patterns, and automated meal kit assembly scheduling. This reduced food waste by 40% and improved delivery times by 30%.",
      impact: [
        "40% reduction in food waste",
        "30% improvement in delivery times",
        "Automated demand forecasting with 95% accuracy",
        "Streamlined supplier coordination and ordering",
      ],
    },
  ];

  return (
    <>
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f7f1e8]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="space-y-4">
                {/* Card */}
                <div
                  onClick={() => setSelectedCase(index)}
                  className={`rounded-3xl h-64 relative overflow-hidden bg-gradient-to-br ${study.gradient} flex items-center justify-center cursor-pointer hover:scale-105 transition-transform shadow-md`}
                >
                  {/* Industry Tag */}
                  <div className="absolute top-6 right-6 bg-white/85 rounded-full px-3 py-1.5 shadow-sm">
                    <span className="text-xs font-semibold text-gray-800">
                      {study.industry}
                    </span>
                  </div>

                  {/* Company Name */}
                  <div className="text-white text-4xl font-light flex items-center gap-2">
                    {study.name === "Hamilton" && "✱"}
                    {study.name === "Terra Foods" && "🌿"}
                    {study.name}
                  </div>
                </div>

                {/* Description */}
                <div className="bg-[#f2e9dd] rounded-2xl p-6 flex justify-between items-start gap-4 shadow-sm">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {study.name}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {study.description}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedCase(index)}
                    className="flex-shrink-0 w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                  >
                    →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedCase !== null && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedCase(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedCase(null)}
              className="absolute top-6 right-6 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors z-10"
            >
              <X size={20} />
            </button>

            {/* Modal Header - Gradient Banner */}
            <div
              className={`h-48 bg-gradient-to-br ${caseStudies[selectedCase].gradient} flex items-center justify-center rounded-t-3xl`}
            >
              <div className="text-white text-5xl font-light">
                {caseStudies[selectedCase].name}
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              <div className="mb-4">
                <span className="inline-block bg-gray-100 rounded-full px-4 py-2 text-sm font-medium text-gray-900">
                  {caseStudies[selectedCase].industry}
                </span>
              </div>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                The Challenge
              </h3>
              <p className="text-gray-600 leading-relaxed mb-8">
                {caseStudies[selectedCase].fullDescription}
              </p>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Impact & Results
              </h3>
              <ul className="space-y-3">
                {caseStudies[selectedCase].impact.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      ✓
                    </div>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <a
                  href="https://calendly.com/rohansontakke/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                >
                  Book a free call →
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CaseStudyCards;
